import { FieldConfig } from '@/lib/engineTypes';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Info } from 'lucide-react';
import React, { useState, forwardRef } from 'react';

interface Props {
  field: FieldConfig;
  value: string | number | boolean;
  onChange: (value: string) => void;
}

function Tooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex">
      <Info
        className="w-3 h-3 text-muted-foreground/30 cursor-help hover:text-primary/60 transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <div className="absolute left-5 top-1/2 -translate-y-1/2 z-50 w-44 px-2.5 py-1.5 rounded-md bg-popover/95 border border-primary/20 text-[9px] text-popover-foreground shadow-xl backdrop-blur-sm leading-relaxed">
          {text}
        </div>
      )}
    </div>
  );
}

const FieldLabel = forwardRef<HTMLDivElement, { label: string; tooltip?: string }>(
  ({ label, tooltip }, ref) => (
    <div ref={ref} className="flex items-center gap-1.5">
      <label className="text-[9px] font-mono text-muted-foreground/80 uppercase tracking-wider leading-none">{label}</label>
      {tooltip && <Tooltip text={tooltip} />}
    </div>
  )
);
FieldLabel.displayName = 'FieldLabel';

export default function ControlField({ field, value, onChange }: Props) {
  if (field.type === 'select') {
    return (
      <div className="space-y-1.5">
        <FieldLabel label={field.label} tooltip={field.tooltip} />
        <select
          value={String(value)}
          onChange={e => onChange(e.target.value)}
          className="w-full rounded-md border border-border/60 bg-input/80 px-2.5 py-[7px] text-[11px] text-foreground focus:ring-1 focus:ring-primary/50 focus:border-primary/40 outline-none transition-all hover:border-border"
        >
          {/* when the underlying value is 'none' we insert a hidden placeholder
              option so the select shows blank but the option isn’t visible in the
              dropdown. this keeps the UI clean when nothing has been chosen yet. */}
          <option value="none" disabled hidden />
          {field.options
            ?.filter(o => o.value !== 'none')
            .map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
      </div>
    );
  }

  if (field.type === 'slider') {
    const numVal = typeof value === 'number' ? value : parseInt(String(value)) || field.min || 0;
    const unit = field.max === 360 ? '°' : field.max === 9000 ? 'K' : field.max === 45 ? '°' : '%';
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <FieldLabel label={field.label} tooltip={field.tooltip} />
          <span className="text-[10px] font-mono text-primary/80 tabular-nums">{numVal}{unit}</span>
        </div>
        <Slider
          value={[numVal]}
          min={field.min ?? 0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          onValueChange={([v]) => onChange(String(v))}
          className="w-full"
        />
      </div>
    );
  }

  if (field.type === 'toggle') {
    return (
      <div className="flex items-center justify-between py-1">
        <FieldLabel label={field.label} tooltip={field.tooltip} />
        <Switch
          checked={value === true || value === 'true'}
          onCheckedChange={v => onChange(String(v))}
        />
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div className="space-y-1.5">
        <FieldLabel label={field.label} tooltip={field.tooltip} />
        <input
          value={String(value || '')}
          onChange={e => onChange(e.target.value)}
          placeholder={`Enter ${field.label.toLowerCase()}...`}
          className="w-full rounded-md border border-border/60 bg-input/80 px-2.5 py-[7px] text-[11px] text-foreground focus:ring-1 focus:ring-primary/50 focus:border-primary/40 outline-none placeholder:text-muted-foreground/30 transition-all hover:border-border"
        />
      </div>
    );
  }

  return null;
}
